import { z } from 'zod'
import { detectionProfileSchema, type DetectionProfile } from '@/features/detection/domain/profile'
import { allRulesOn, ruleIds, type EngineRules } from '@/features/detection/domain/rules'

// Profil de calibration modifié sur la tablette (TEMPORAIRE, lot O7, jamais en production). Il remplace localement le
// profil figé de la mission pour le moteur de la tablette seulement : jamais envoyé au serveur, jamais en AUTO (le
// moteur ne fait qu'observer, le maintien de 3 s reste la seule confirmation).

export interface ProfileOverride {
  profile: DetectionProfile
  rules: EngineRules
  /** Profil du serveur sur lequel la calibration a été faite; `null` : aucun (base Démo). */
  basedOn: ProfileRef | null
}

export interface ProfileRef {
  id: string
  version: number
}

const storageKey = 'reca-operator-v3.detection-override'

const rulesSchema = z.object(Object.fromEntries(ruleIds.map((id) => [id, z.boolean()])))

const refSchema = z.object({ id: z.string().min(1), version: z.number().int().positive() }).nullable()

/** Le mode AUTO n'est jamais accepté ici : ramené à OBSERVATION. */
export function sanitizeOverride(
  profile: DetectionProfile,
  rules: EngineRules,
  basedOn: ProfileRef | null,
): ProfileOverride {
  return {
    profile: { ...profile, mode: profile.mode === 'AUTO' ? 'OBSERVATION' : profile.mode },
    rules: { ...rules },
    basedOn: basedOn ? { id: basedOn.id, version: basedOn.version } : null,
  }
}

/**
 * Calibration encore valable pour le profil du serveur de la mission ouverte. Faite sur un autre profil (nouvelle
 * version publiée au bureau, passage en AUTO) ou sans référence (calibration antérieure), elle est ignorée : une
 * calibration oubliée sur la tablette ne doit jamais masquer le profil du serveur.
 */
export function overrideFor(override: ProfileOverride | null, server: DetectionProfile | null): ProfileOverride | null {
  if (!override) return null
  const { basedOn } = override
  if (!basedOn || !server) return basedOn === null && server === null ? override : null
  return basedOn.id === server.id && basedOn.version === server.version ? override : null
}

export function readProfileOverride(): ProfileOverride | null {
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null
    const value = JSON.parse(raw) as { profile?: unknown; rules?: unknown; basedOn?: unknown }
    const profile = detectionProfileSchema.safeParse(value.profile)
    if (!profile.success) return null
    // Calibration antérieure sans référence : faite sur un profil inconnu, jamais réappliquée.
    const basedOn = refSchema.safeParse(value.basedOn)
    if (!basedOn.success) return null
    const rules = rulesSchema.safeParse(value.rules ?? {})
    return sanitizeOverride(profile.data, rules.success ? { ...allRulesOn, ...rules.data } : allRulesOn, basedOn.data)
  } catch {
    return null
  }
}

export function writeProfileOverride(override: ProfileOverride | null): void {
  try {
    if (override) window.localStorage.setItem(storageKey, JSON.stringify(override))
    else window.localStorage.removeItem(storageKey)
  } catch {
    // calibration non mémorisée : elle vaut pour la page ouverte
  }
}
